// Builds the browser tab title so users can tell at a glance which page a tab is on.
// Format: "<Page Name> - GWI360" with an environment marker appended outside production.
import { findTableByRoute } from '../config/dataTables.registry';

const BASE = 'GWI360';

function envSuffix(): string {
  const env = import.meta.env.VITE_ENV;
  if (env === 'test') return ' - Test';
  if (env === 'local' || import.meta.env.DEV) return ' - Local';
  return '';
}

// Names for routes that don't appear in the sidebar navigation (dynamic detail
// pages, search results, etc.) and therefore can't be resolved via findNavItemByHref.
function fallbackName(pathname: string): string | null {
  const staticNames: Record<string, string> = {
    '/home': 'Home',
    '/communications': 'Communications',
    '/searchresults': 'Search Results',
    '/my-tickets': 'My Tickets',
    '/roadmap': 'Roadmap',
    '/progress/tracker': 'Progress Tracker',
    '/monitor': 'Network Monitor',
    // Order Management children — reachable from the Quotes page, not the sidebar.
    '/order-management/event-history': 'Event History',
    '/order-management/salesforce-import': 'Salesforce Import',
    '/order-management/salesforce-export': 'Salesforce Export',
    // Guard-only /yard tree. It renders outside the employee shell, so its titles
    // come from here via GuardLayout's own usePageTitle call.
    '/yard/inventory': 'Yard Inventory',
    '/yard/reefer-log': 'Reefer Log',
    '/yard/reports': 'Yard Reports',
    '/yard/change-password': 'Change Password',
  };
  if (staticNames[pathname]) return staticNames[pathname];

  // Data Management tables. Every /data-management/<slug> route is generated from
  // DATA_TABLES, so the title comes from the same registry entry that named the
  // page — a table added there gets its tab title with no second edit. Before this,
  // all 106 of them showed a bare "GWI360": the shell resolves titles from SIDEBAR
  // entries, and these tables are reached through the hub page, not the sidebar.
  if (pathname.startsWith('/data-management/')) {
    const table = findTableByRoute(pathname);
    if (table) return table.label;
  }

  const proMatch = pathname.match(/^\/pro\/(.+)$/);
  if (proMatch) return `PRO ${decodeURIComponent(proMatch[1])}`;

  const orderMatch = pathname.match(/^\/order-details\/(.+)$/);
  if (orderMatch) return `Order ${decodeURIComponent(orderMatch[1])}`;

  // TPRM vendor pages: /compliance/tprm/vendors/{new | <id>[/edit|/report]}. The id
  // is a surrogate key that would mean nothing on a tab, so these name the ACTION.
  const vendorMatch = pathname.match(/^\/compliance\/tprm\/vendors\/([^/]+)(?:\/(edit|report))?$/);
  if (vendorMatch) {
    if (vendorMatch[1] === 'new') return 'New Vendor';
    if (vendorMatch[2] === 'edit') return 'Edit Vendor';
    if (vendorMatch[2] === 'report') return 'Vendor Report';
    return 'Vendor Details';
  }

  // A quote group is keyed by customer + bill-to, neither of which is a name here.
  if (/^\/order-management\/quotes\/group\//.test(pathname)) return 'Quote Group';

  // /order-management/quotes/<id> only redirects to the grouped page, but it is the
  // one redirect people arrive at COLD (a shared or bookmarked deep link), so it is
  // named rather than flashing a bare product title while auth and the lookup
  // resolve. The two compliance redirects are internal hops and are left alone.
  // The [^/]+ keeps this off the /quotes/group/... path above.
  const quoteMatch = pathname.match(/^\/order-management\/quotes\/([^/]+)$/);
  if (quoteMatch) return `Quote ${decodeURIComponent(quoteMatch[1])}`;

  return null;
}

/**
 * Build the full document title for the current page.
 * @param pageName Name resolved from the sidebar navigation, or null if not found.
 * @param pathname Current location pathname, used to resolve non-nav routes.
 */
export function buildPageTitle(pageName: string | null, pathname: string): string {
  const name = pageName ?? fallbackName(pathname);
  return name ? `${name} - ${BASE}${envSuffix()}` : `${BASE}${envSuffix()}`;
}
